/**
 * Footer legal links: shows the "Cookie preferences" button once Shopify's cookie banner API is available and
 * reopens the preferences window when it is clicked. Without the banner (for example in regions where it is not
 * required, or while it is turned off in Settings > Customer privacy) the button stays hidden.
 *
 * Markup hooks: `[data-cookie-preferences]` (the list item, hidden by default) containing a `button`.
 */
class FooterLegalLinks extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    const item = this.querySelector('[data-cookie-preferences]');
    const button = item?.querySelector('button');
    if (!item || !button) return;

    this.#abort = new AbortController();

    const isAvailable = () => typeof window.privacyBanner?.showPreferences === 'function';
    const reveal = () => {
      if (!isAvailable()) return false;
      item.hidden = false;
      return true;
    };

    // The banner script can load after this component, so look for it for a few seconds
    if (!reveal()) {
      let attempts = 0;
      this.#timer = window.setInterval(() => {
        attempts += 1;
        if (reveal() || attempts >= 20) window.clearInterval(this.#timer);
      }, 500);
    }

    button.addEventListener(
      'click',
      () => {
        if (!isAvailable()) return;
        Promise.resolve(window.privacyBanner.showPreferences()).catch(() => {});
      },
      { signal: this.#abort.signal }
    );
  }

  disconnectedCallback() {
    this.#abort?.abort();
    window.clearInterval(this.#timer);
  }
}

if (!customElements.get('footer-legal-links')) {
  customElements.define('footer-legal-links', FooterLegalLinks);
}
